// Coach Career tab — pure data builder (Coach-Profile-Data, Marin 2026-08-20).
//
// Merges the TheSports coaching resume (BE PR #442: `resume` = stations
// newest-first, {team{id: platform id|null, name, logo}, position_label,
// joined, contract_until (epoch seconds), matches, win, draw, lose,
// players_used, ppm, goal_ppt}) with the coach's SELF-ENTERED experiences
// (platform profile). Self entries WIN: a TS station is dropped when a self
// entry names the same club (normalized) and their periods overlap — the
// platform-wide precedence rule (own data over provider feed).

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Epoch seconds -> "Oct 2025"; null/0 -> null (empty renders nothing).
export const formatMonthYear = (epochSeconds) => {
  if (!epochSeconds || !Number.isFinite(Number(epochSeconds))) return null;
  const d = new Date(Number(epochSeconds) * 1000);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const yearOf = (epochSeconds) =>
  epochSeconds && Number.isFinite(Number(epochSeconds))
    ? new Date(Number(epochSeconds) * 1000).getUTCFullYear()
    : null;

const normClub = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

// Self experience period years (transform in CoachProfile delivers
// start_date/end_date as "MM/YYYY" or "Present").
const selfYears = (exp) => {
  const parse = (s) => {
    const m = /(\d{4})/.exec(String(s || ""));
    return m ? Number(m[1]) : null;
  };
  const start = parse(exp.start_date);
  const end =
    String(exp.end_date || "").toLowerCase() === "present"
      ? new Date().getUTCFullYear()
      : parse(exp.end_date);
  return { start, end };
};

const periodsOverlap = (aStart, aEnd, bStart, bEnd) => {
  if (aStart == null || bStart == null) return false;
  const ae = aEnd == null ? aStart : aEnd;
  const be = bEnd == null ? bStart : bEnd;
  return aStart <= be && bStart <= ae;
};

// -> [{source:'self'|'ts', key, clubName, clubLogo, teamId, positionLabel,
//      periodText, place?, matches?, win?, draw?, lose?, ppm?, goalPpt?,
//      playersUsed?, sortKey}]
// newest-first; TS stations that a self entry overrides are dropped.
export const buildCareerEntries = (resume, selfExperience) => {
  const stations = Array.isArray(resume) ? resume : [];
  const selfEntries = Array.isArray(selfExperience) ? selfExperience : [];

  const self = selfEntries.map((exp, i) => {
    const { start, end } = selfYears(exp);
    return {
      source: "self",
      key: `self-${exp.id ?? i}`,
      clubName: exp.club_name || "",
      clubLogo: null,
      teamId: null,
      positionLabel: exp.position_name || "",
      periodText: [exp.start_date, exp.end_date].filter(Boolean).join(" – "),
      place: exp.place || null,
      _startYear: start,
      _endYear: end,
      sortKey: start || 0,
    };
  });

  const ts = stations
    .filter((st) => {
      const stStart = yearOf(st.joined);
      const stEnd = yearOf(st.contract_until);
      return !self.some(
        (s) =>
          normClub(s.clubName) &&
          normClub(s.clubName) === normClub(st.team && st.team.name) &&
          periodsOverlap(s._startYear, s._endYear, stStart, stEnd)
      );
    })
    .map((st, i) => {
      const from = formatMonthYear(st.joined);
      const until = formatMonthYear(st.contract_until);
      return {
        source: "ts",
        key: `ts-${(st.team && st.team.name) || "station"}-${st.joined || i}`,
        clubName: (st.team && st.team.name) || "",
        clubLogo: (st.team && st.team.logo) || null,
        teamId: (st.team && st.team.id) || null,
        positionLabel: st.position_label || "",
        periodText: [from, until].filter(Boolean).join(" – "),
        matches: st.matches ?? null,
        win: st.win ?? null,
        draw: st.draw ?? null,
        lose: st.lose ?? null,
        ppm: st.ppm ?? null,
        goalPpt: st.goal_ppt ?? null,
        playersUsed: st.players_used ?? null,
        sortKey: yearOf(st.joined) || 0,
      };
    });

  return [...self, ...ts].sort((a, b) => b.sortKey - a.sortKey);
};

export default buildCareerEntries;
